import React from 'react';

import { IInputProps as IInputBaseProps, Input, inputClasses } from '@private/components';
import { getBaseThemeProps, getFontBase, getPrefixedVar } from '@private/payment';
import styled, { css } from 'styled-components';

import { ThemedElement } from 'utils/types';

export type IInputProps = ThemedElement<
  IInputBaseProps &
    INewProps & {
      hintIcon?: React.ReactNode;
      sizeVariant?: 'lg' | 'md' | 'sm';
    }
>;

export interface INewProps {
  disablePasswordIcon?: boolean;
  hintText?: string;
  isAlignCenter?: boolean;
  isAutoFocused?: boolean;
  maxHintWidth?: number;
  maxNumber?: number;
  regExp?: RegExp;
  shouldValidate?: boolean;
}

export type IStyledInputProps = ThemedElement<
  IInputBaseProps &
    INewProps & {
      $sizeVariant?: 'lg' | 'md' | 'sm';
    }
>;
type IInputThemeProps = Pick<IInputBaseProps, 'disabled' | 'error' | 'startIcon'>;
type IThemedInputProps = IInputThemeProps & INewProps;

export const CustomLabel = styled.div`
  display: flex;
  align-items: center;
  column-gap: 10px;
`;

export const Label = styled.div``;

const getInputBaseProps = (prefix: string[]) => css`
  ${getBaseThemeProps(prefix)};

  background: ${getPrefixedVar(prefix, 'bg')};
  box-shadow:
    0 0 0 transparent,
    0 0 0 ${getPrefixedVar(prefix, 'outlineWidth')}
      ${getPrefixedVar(prefix, 'outlineColor')};
`;

export const getInputTheme = (prefix = ['components', 'input', 'lg']) => {
  return css<IThemedInputProps>`
    ${inputClasses.iconButton} {
      min-height: auto;
    }

    ${inputClasses.error} {
      margin-top: 2px;
      min-height: 18px;
    }

    ${inputClasses.labelPlaceholder} {
      line-height: 133%;
    }

    ${inputClasses.label}, ${Label} {
      line-height: 143%;
      max-height: 20px;
      overflow: hidden;
    }

    ${({ error: isError, isAlignCenter }) => {
      if (isError) {
        return css`
          ${inputClasses.input}, ${inputClasses.input}:not(:read-only):focus {
            ${getInputBaseProps([...prefix, 'error'])};
          }

          ${inputClasses.input}::placeholder {
            ${getFontBase([...prefix, 'error', 'placeholder'])};
          }

          ${inputClasses.labelPlaceholder}, ${inputClasses.labelPlaceholder}[data-is-active='true'] {
            ${getFontBase([...prefix, 'error', 'labelPlaceholder'])};
          }

          ${inputClasses.label}, ${Label} {
            ${getFontBase([...prefix, 'error', 'label'])};
          }

          ${inputClasses.iconWrapper}:first-of-type {
            color: ${getPrefixedVar(prefix, 'error', 'startIcon')};
            transition: all 0.3s ease;
          }

          ${inputClasses.iconWrapper}:last-of-type {
            color: ${getPrefixedVar(prefix, 'error', 'endIcon')};
            transition: all 0.3s ease;

            button {
              color: inherit;
            }
          }

          &:focus-within ${inputClasses.labelPlaceholder},
          ${inputClasses.labelPlaceholder}[data-is-active='true'] {
            font-size: ${getPrefixedVar(prefix, 'focus', 'labelPlaceholder', 'fontSize')};
          }

          ${inputClasses.error} {
            ${getFontBase([...prefix, 'error', 'helpText'])};
          }
        `;
      }

      return css`
        ${inputClasses.labelPlaceholder} {
          ${getFontBase([...prefix, 'inactive', 'labelPlaceholder'])};
        }

        ${inputClasses.label}, ${Label} {
          ${getFontBase([...prefix, 'inactive', 'label'])};
        }

        &:focus-within ${inputClasses.labelPlaceholder},
    ${inputClasses.labelPlaceholder}[data-is-active='true'] {
          top: 10px;
          ${getFontBase([...prefix, 'focus', 'labelPlaceholder'])};
        }

        &:focus-within ${inputClasses.label}, &:focus-within ${Label} {
          ${getFontBase([...prefix, 'focus', 'label'])};
        }

        ${isAlignCenter &&
        css`
          ${inputClasses.labelPlaceholder} {
            left: 50%;
            transform: translate(-50%, -50%);
          }

          ${inputClasses.input} {
            text-align: center;
            padding-left: 5px;
            padding-right: 5px;
          }
        `}

        ${inputClasses.iconWrapper}:first-of-type {
          color: ${getPrefixedVar(prefix, 'inactive', 'startIcon')};
          transition: all 0.3s ease;
        }

        ${inputClasses.iconWrapper}:last-of-type {
          color: ${getPrefixedVar(prefix, 'inactive', 'endIcon')};
          transition: all 0.3s ease;

          button {
            color: inherit;
          }
        }

        &:hover ${inputClasses.iconWrapper} {
          &:first-of-type {
            color: ${getPrefixedVar(prefix, 'hover', 'startIcon')};
          }

          &:last-of-type {
            color: ${getPrefixedVar(prefix, 'hover', 'endIcon')};
          }
        }

        ${inputClasses.input} {
          ${getInputBaseProps([...prefix, 'inactive'])};

          &:-webkit-autofill {
            box-shadow: 0 0 0 1000px ${getPrefixedVar(prefix, 'inactive', 'bg')} inset !important;
            -webkit-text-fill-color: ${getPrefixedVar(prefix, 'inactive', 'text')};
          }
        }

        ${inputClasses.input}:focus ~ ${inputClasses.iconWrapper} {
          &:first-of-type {
            color: ${getPrefixedVar(prefix, 'focus', 'startIcon')};
          }

          &:last-of-type {
            color: ${getPrefixedVar(prefix, 'focus', 'endIcon')};
          }
        }

        ${inputClasses.input}:disabled ~ ${inputClasses.iconWrapper} {
          &:first-of-type {
            color: ${getPrefixedVar(prefix, 'disabled', 'startIcon')};
          }

          &:last-of-type {
            color: ${getPrefixedVar(prefix, 'disabled', 'endIcon')};
          }
        }

        ${inputClasses.input}::placeholder {
          ${getFontBase([...prefix, 'inactive', 'placeholder'])};
        }

        &:hover ${inputClasses.input}:not(:read-only) {
          ${getInputBaseProps([...prefix, 'hover'])};
        }

        &:hover ${inputClasses.input}:not(:read-only)::placeholder {
          ${getFontBase([...prefix, 'hover', 'placeholder'])};
        }

        ${inputClasses.input}:not(:read-only) {
          &:focus {
            ${getInputBaseProps([...prefix, 'focus'])};

            &::placeholder {
              ${getFontBase([...prefix, 'focus', 'placeholder'])};
            }
          }

          &:disabled {
            ${getInputBaseProps([...prefix, 'disabled'])};

            &::placeholder {
              ${getFontBase([...prefix, 'disabled', 'placeholder'])};
            }
          }
        }

        ${inputClasses.input} {
          &:disabled {
            ${getInputBaseProps([...prefix, 'disabled'])};

            &::placeholder {
              ${getFontBase([...prefix, 'disabled', 'placeholder'])};
            }
          }
        }

        ${inputClasses.iconButton} {
          color: ${getPrefixedVar(prefix, 'button', 'base', 'icon')};
          background: ${getPrefixedVar(prefix, 'button', 'base', 'bg')};

          &:hover {
            color: ${getPrefixedVar(prefix, 'button', 'hover', 'icon')};
            background: ${getPrefixedVar(prefix, 'button', 'hover', 'bg')};
          }

          &:active {
            color: ${getPrefixedVar(prefix, 'button', 'pressed', 'icon')};
            background: ${getPrefixedVar(prefix, 'button', 'hover', 'bg')};
          }
        }
      `;
    }}

    ${({ disabled: isDisabled }) =>
      isDisabled &&
      css`
        ${inputClasses.labelPlaceholder}, ${inputClasses.labelPlaceholder}[data-is-active='true'] {
          ${getFontBase([...prefix, 'disabled', 'labelPlaceholder'])};
        }
        ${inputClasses.label}, ${Label} {
          ${getFontBase([...prefix, 'disabled', 'label'])};
        }
      `}
  `;
};

export const StyledInput = styled(Input).attrs<IStyledInputProps>((props) => ({
  endIconSize: 19,
  iconMargin: 16,
  isAnimatedLabel: true,
  startIconSize: 19,
  ...props,
}))<IInputProps>`
  row-gap: 7px;

  ${inputClasses.labelPlaceholder} {
    ${({ startIcon }) =>
      !startIcon &&
      css`
        left: 16px;
      `};
  }

  ${inputClasses.input} {
    ${({ isAnimatedLabel, startIcon }) =>
      !startIcon &&
      isAnimatedLabel &&
      css`
        padding-left: 16px;
      `};
  }
`;

export const LgInput = styled(StyledInput)<IInputProps>`
  ${({ themePrefix = ['components', 'input'] }) => {
    return css`
      ${getInputTheme([...themePrefix, 'lg'])};

      ${inputClasses.input} {
        height: 48px;
      }
    `;
  }}
`;

export const SmInput = styled(StyledInput)<IInputProps>`
  ${({ themePrefix = ['components', 'input'] }) => {
    return css`
      ${getInputTheme([...themePrefix, 'sm'])};

      ${inputClasses.input} {
        height: 32px;
      }
    `;
  }}
`;

export const MdInput = styled(StyledInput)<IInputProps>`
  ${({ themePrefix = ['components', 'input'] }) => {
    return css`
      ${getInputTheme([...themePrefix, 'md'])};

      ${inputClasses.input} {
        height: 40px;
      }
    `;
  }}
`;
